import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import BackToTop from './BackToTop';
import SEO from './SEO';
import { useTheme } from '../context/ThemeContext';

const InsightsPage = () => {
  const { isDarkMode } = useTheme();
  const [hoveredCard, setHoveredCard] = useState(null);
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch insights from API
  useEffect(() => {
    const fetchInsights = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/insights`);
        if (!response.ok) throw new Error('Failed to fetch insights');
        const data = await response.json();
        // Transform API data to match component structure
        const transformedData = data.map(item => ({
          id: item.id,
          category: item.category,
          title: item.title,
          excerpt: item.excerpt,
          date: item.date,
          readTime: item.read_time,
          color: item.color,
          imageUrl: item.image_url,
        }));
        setInsights(transformedData);
      } catch (err) {
        console.error('Error fetching insights:', err);
        setError(err.message);
        // Fallback to static data if API fails
        setInsights([
          {
            id: '1',
            category: 'Personal Branding',
            title: 'Why Your Personal Brand is Your Most Valuable Asset in 2025',
            excerpt: 'In an age of AI-generated content, authenticity becomes your competitive advantage. Learn how to stand out.',
            date: 'Dec 15, 2024',
            readTime: '5 min read',
            color: '#F6CD17',
            imageUrl: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&q=80',
          },
          {
            id: '2',
            category: 'Strategy',
            title: 'The Art of Saying No: How Constraints Drive Creative Excellence',
            excerpt: 'Discover how limiting your options can actually unlock more creative and impactful marketing strategies.',
            date: 'Dec 10, 2024',
            readTime: '7 min read',
            color: '#35392F',
            imageUrl: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=400&q=80',
          },
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchInsights();
  }, []);

  const bgClass = isDarkMode ? 'bg-[#0A0A0A]' : 'bg-[#FAFAFA]';
  const textClass = isDarkMode ? 'text-[#F2F2F2]' : 'text-[#1A1A1A]';
  const subtextClass = isDarkMode ? 'text-[#999999]' : 'text-[#555555]';
  const cardBgClass = isDarkMode ? 'bg-[#141414]' : 'bg-white';
  const borderClass = isDarkMode ? 'border-white/10' : 'border-[#E5E5E5]';

  return (
    <div className={`min-h-screen ${bgClass} ${textClass}`}>
      {/* SEO */}
      <SEO 
        title="Insights - Marketing Strategy & Brand Building"
        description="Expert insights on efficient brand building, AI in marketing, and strategies that maximize ROI. Practical thinking for business leaders who value results."
        canonical="/insights"
      />
      
      <Header />
      <BackToTop />

      {/* Hero Section */}
      <section className="pt-32 pb-12 px-6 md:px-8">
        <div className="max-w-7xl mx-auto">
          <span className={`text-xs uppercase tracking-widest ${subtextClass} block mb-6`}>
            INSIGHTS
          </span>
          <h1 className={`headline-xl text-[9vw] md:text-[6vw] lg:text-[5vw] ${textClass} mb-8`}>
            Thoughts on Building
            <br />
            <span className="text-[#F6CD17]">Brands That Matter</span>
          </h1>
          <p className={`text-lg md:text-xl ${subtextClass} max-w-2xl leading-relaxed`}>
            Perspectives on strategy, personal branding, and navigating the new landscape of marketing.
          </p>
        </div>
      </section>

      {/* Articles Count */}
      <section className="px-6 md:px-8 pb-8">
        <div className="max-w-7xl mx-auto">
          <div className={`flex items-center gap-4 pb-6 border-b ${borderClass}`}>
            <span className={`text-sm ${subtextClass}`}>
              {insights.length} articles
            </span>
            <span className={`text-sm ${subtextClass}`}>•</span>
            <span className={`text-sm ${subtextClass}`}>
              Explore all thought leadership content
            </span>
          </div>
        </div>
      </section>

      {/* Featured Insights Grid with Thumbnails */}
      <section className="py-8 px-6 md:px-8">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#F6CD17]"></div>
            </div>
          ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {insights.map((insight) => (
              <Link
                to={`/insights/${insight.id}`}
                key={insight.id}
                className={`group relative ${cardBgClass} border ${borderClass} rounded-2xl overflow-hidden cursor-pointer block hover:shadow-xl`}
                onMouseEnter={() => setHoveredCard(insight.id)}
                onMouseLeave={() => setHoveredCard(null)}
                style={{
                  transform: hoveredCard === insight.id 
                    ? 'translateY(-8px)' 
                    : 'translateY(0)',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                }}
              >
                {/* Thumbnail Image */}
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={insight.imageUrl || `https://images.unsplash.com/photo-1557804506-669a67965ba0?w=400&q=80`}
                    alt={`${insight.title} - ${insight.category} article thumbnail featuring abstract business imagery`}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  {/* Gradient Overlay */}
                  <div 
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    style={{
                      background: `linear-gradient(to top, ${insight.color}40, transparent)`,
                    }}
                  />
                  {/* Category Badge on Image */}
                  <span 
                    className="absolute top-4 left-4 text-xs font-medium uppercase tracking-wider px-3 py-1.5 rounded-full backdrop-blur-sm"
                    style={{ 
                      backgroundColor: `${insight.color}dd`,
                      color: insight.color === '#F6CD17' ? '#000' : '#fff',
                    }}
                  >
                    {insight.category}
                  </span>
                </div>
                
                {/* Card Content */}
                <div className="p-6">
                  {/* Title */}
                  <h2 
                    className={`font-display text-lg md:text-xl ${textClass} mb-3 leading-[1.5] pt-1 group-hover:text-[#F6CD17] transition-colors duration-300 line-clamp-2`}
                  >
                    {insight.title}
                  </h2>

                  {/* Excerpt */}
                  <p className={`${subtextClass} text-sm leading-relaxed mb-4 line-clamp-2`}>
                    {insight.excerpt}
                  </p>

                  {/* Meta Info */}
                  <div className={`flex items-center justify-between pt-4 border-t ${borderClass}`}>
                    <div className="flex items-center gap-3">
                      <span className={`text-xs ${subtextClass}`}>{insight.date}</span>
                      <span className={`text-xs ${subtextClass}`}>•</span>
                      <span className={`text-xs ${subtextClass}`}>{insight.readTime}</span>
                    </div>
                    <svg 
                      width="20" 
                      height="20" 
                      viewBox="0 0 16 16" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="1.5"
                      className="transition-transform duration-300 group-hover:translate-x-1"
                      style={{ color: insight.color }}
                    >
                      <path d="M3 8H13M13 8L9 4M13 8L9 12" />
                    </svg>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          )}
        </div>
      </section>

      {/* Newsletter CTA Section */}
      <section className="py-24 px-6 md:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <span className={`text-xs uppercase tracking-widest ${subtextClass} block mb-4`}>
            STAY UPDATED
          </span>
          <h2 className={`font-display text-3xl md:text-4xl ${textClass} mb-6`}>
            Get insights delivered to your inbox
          </h2>
          <p className={`${subtextClass} mb-8 max-w-lg mx-auto`}>
            No spam, just thoughtful perspectives on building brands that resonate.
          </p>
          
          {/* Email Input */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
            <input 
              type="email" 
              placeholder="Your email"
              className={`flex-1 px-6 py-4 rounded-full border ${borderClass} ${cardBgClass} ${textClass} focus:outline-none focus:ring-2 focus:ring-[#F6CD17]/30 transition-all`}
            />
            <button className="glass-button-primary px-8 py-4 rounded-full font-medium">
              Subscribe
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default InsightsPage;
